import dayjs from "dayjs";

export interface FotoTrasladoItem {
  photoUrl: string | null;
  photoId: string | null;
  photoname: string;
  fecha_hora: string; // ISO — fecha/hora de carga
  /** Solo fotos de PROCESO: identifica el punto (inicial / final). */
  tipo?: TipoFotoProceso;
}

/**
 * Reglas de fotos del TRASLADO (solo traslados; instalaciones y visitas no
 * cambian):
 *  - Ingreso: exactamente 6 fotos (al iniciar).
 *  - Proceso: 2 fotos con título fijo — "Foto punto inicial" y "Foto punto final".
 *  - Finalización: exactamente 6 fotos (al finalizar).
 */
export const FOTOS_INGRESO_REQUERIDAS = 6;
export const FOTOS_FINALIZACION_REQUERIDAS = 6;

export type TipoFotoProceso = "PUNTO_INICIAL" | "PUNTO_FINAL";

export const FOTOS_PROCESO_SLOTS: { tipo: TipoFotoProceso; titulo: string; ayuda: string }[] = [
  { tipo: "PUNTO_INICIAL", titulo: "Foto punto inicial", ayuda: "Punto donde inicia el tendido/traslado del servicio." },
  { tipo: "PUNTO_FINAL", titulo: "Foto punto final", ayuda: "Punto donde termina el tendido/traslado del servicio." },
];

/**
 * Sube un array de File a MinIO (via handleUploadFile) y devuelve el array de
 * items {photoUrl, photoId, photoname, fecha_hora} para persistir en la OT.
 */
export async function subirFotosTraslado(
  files: File[],
  path: string,
  handleUploadFile: (f: File | null, p: string) => Promise<any>,
  prefijo = "Foto"
): Promise<FotoTrasladoItem[]> {
  const results = await Promise.all(files.map((f) => handleUploadFile(f, path)));
  return results.map((r, i) => ({
    photoUrl: r?.url || null,
    photoId: r?.id_archivos || r?.url || null,
    photoname: `${prefijo} ${i + 1}`,
    fecha_hora: dayjs().toISOString(),
  }));
}

/** URLs (no nulas) de un array de fotos guardado en la OT. */
export function fotosTrasladoUrls(arr: any): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((f) => f?.photoUrl).map((f) => f.photoUrl as string);
}

/** Fotos de proceso guardadas en la OT (array limpio). */
export function fotosProcesoDe(ordenTrabajo: any): FotoTrasladoItem[] {
  const arr = ordenTrabajo?.urls_fotos_proceso;
  return Array.isArray(arr) ? arr.filter((f: any) => f?.photoUrl) : [];
}

/** Foto de proceso de un tipo (punto inicial / final), si ya fue cargada. */
export function fotoProcesoPorTipo(ordenTrabajo: any, tipo: TipoFotoProceso): FotoTrasladoItem | null {
  return fotosProcesoDe(ordenTrabajo).find((f) => f.tipo === tipo) || null;
}

/** Títulos de las fotos de proceso que aún faltan (vacío = completas). */
export function fotosProcesoFaltantes(ordenTrabajo: any): string[] {
  return FOTOS_PROCESO_SLOTS.filter((s) => !fotoProcesoPorTipo(ordenTrabajo, s.tipo)).map((s) => s.titulo);
}

export function fotosProcesoCompletas(ordenTrabajo: any): boolean {
  return fotosProcesoFaltantes(ordenTrabajo).length === 0;
}

/** Detecta si una OT corresponde al flujo de TRASLADO.
 * Robusto: mira varias fuentes por si `agendamiento_data` viene incompleto en
 * algún registro (p. ej. cache de lista vs detalle): agendamiento_data,
 * agendamiento (crudo) y el propio `tipo_orden_trabajo` de la OT. Instalaciones
 * (tipo NULL/'INSTALACIÓN') y visitas ('VISITA') NO matchean, así que no se
 * afecta esos flujos. */
export function esOrdenTraslado(ordenTrabajo: any): boolean {
  const T = "TRASLADO";
  return (
    ordenTrabajo?.agendamiento_data?.tipo_orden_servicio === T ||
    ordenTrabajo?.agendamiento?.tipo_orden_servicio === T ||
    ordenTrabajo?.tipo_orden_trabajo === T
  );
}

// ---------------------------------------------------------------------------
// Fotos eliminadas por Activaciones.
//
// Cuando Activaciones elimina una foto, el backend la sustituye por una MARCA
// (`eliminada: true`, sin `photoUrl`, con quién y cuándo). Esa marca es lo que
// indica que al técnico le falta volver a subir una foto. Así no se confunde
// con traslados antiguos que se registraron con menos fotos.
//  - Ingreso / proceso: la marca ocupa el lugar de la foto dentro del arreglo.
//  - Finalización: las marcas van en `urls_fotos_salida.eliminadas`.
// ---------------------------------------------------------------------------
export type EtapaFotoTraslado = "INGRESO" | "PROCESO" | "FINALIZACION";

export interface FotoTrasladoEliminada {
  eliminada?: boolean;
  photoname?: string;
  photoName?: string;
  /** Solo finalización: clave original dentro de `photosSalida`. */
  clave?: string;
  tipo?: TipoFotoProceso;
  eliminada_por?: string;
  eliminada_fecha?: string;
  photoUrlEliminada?: string;
}

/** Es una marca de foto eliminada (no una foto). */
export function esFotoEliminada(f: any): boolean {
  return !!f && f.eliminada === true && !f.photoUrl;
}

/** Fotos de ingreso guardadas en la OT (sin marcas). */
export function fotosIngresoDe(ordenTrabajo: any): FotoTrasladoItem[] {
  const arr = ordenTrabajo?.urls_fotos_ingreso;
  return Array.isArray(arr) ? arr.filter((f: any) => f?.photoUrl) : [];
}

/** Ingreso en su orden original: fotos y, en su lugar, las marcas de eliminadas. */
export function itemsIngresoDe(ordenTrabajo: any): any[] {
  const arr = ordenTrabajo?.urls_fotos_ingreso;
  return Array.isArray(arr) ? arr.filter((f: any) => f?.photoUrl || esFotoEliminada(f)) : [];
}

/** Fotos de finalización: se guardan como { photosSalida: { fotoFinalizacionN } }. */
export function fotosFinalizacionDe(ordenTrabajo: any): any[] {
  const obj = ordenTrabajo?.urls_fotos_salida?.photosSalida;
  if (!obj || typeof obj !== "object") return [];
  return (Object.values(obj) as any[]).filter((f: any) => f?.photoUrl);
}

/** Marcas de fotos de ingreso eliminadas que el técnico aún no repone. */
export function eliminadasIngresoDe(ordenTrabajo: any): FotoTrasladoEliminada[] {
  const arr = ordenTrabajo?.urls_fotos_ingreso;
  return Array.isArray(arr) ? arr.filter(esFotoEliminada) : [];
}

/** Marca del punto de proceso eliminado (solo si ese punto sigue sin foto). */
export function eliminadaProcesoPorTipo(
  ordenTrabajo: any,
  tipo: TipoFotoProceso,
): FotoTrasladoEliminada | null {
  if (fotoProcesoPorTipo(ordenTrabajo, tipo)) return null;
  const arr = ordenTrabajo?.urls_fotos_proceso;
  if (!Array.isArray(arr)) return null;
  return arr.find((f: any) => esFotoEliminada(f) && f.tipo === tipo) || null;
}

/** Marcas de fotos de finalización eliminadas que el técnico aún no repone. */
export function eliminadasFinalizacionDe(ordenTrabajo: any): FotoTrasladoEliminada[] {
  const salida = ordenTrabajo?.urls_fotos_salida;
  const marcas = salida?.eliminadas;
  if (!Array.isArray(marcas)) return [];
  const photos = salida?.photosSalida && typeof salida.photosSalida === "object" ? salida.photosSalida : {};
  return marcas.filter((m: any) => m?.clave && !photos[m.clave]?.photoUrl);
}

/** El técnico ya inició el traslado (subió las fotos de ingreso). */
export function fotosTrasladoIniciado(ordenTrabajo: any): boolean {
  return !!ordenTrabajo?.fecha_hora_inicio_instalacion;
}

/** El técnico ya finalizó el traslado (OT REALIZADA / REALIZADA_ACTUALIZADA).
 * Solo cuenta el ESTADO: la fecha_hora_fin no sirve de señal porque el
 * Guardar de Activaciones la escribía al gestionar (quedaron OTs con fecha
 * puesta sin que el técnico finalizara) y eso bloqueaba la carga de las
 * fotos de proceso. */
export function fotosTrasladoFinalizado(ordenTrabajo: any): boolean {
  const estado = `${ordenTrabajo?.estado_orden_trabajo || ""}`.toUpperCase();
  return estado.startsWith("REALIZADA");
}

export interface ResumenFotosTraslado {
  ingreso: number;
  proceso: number;
  finalizacion: number;
  iniciado: boolean;
  finalizado: boolean;
  /** Fotos de ingreso que Activaciones eliminó y falta volver a subir. */
  faltanIngreso: number;
  /** Títulos de los puntos de proceso que Activaciones eliminó. */
  procesoEliminadas: string[];
  /** Fotos de finalización que Activaciones eliminó y falta volver a subir. */
  faltanFinalizacion: number;
  /** Hay fotos eliminadas por Activaciones pendientes de volver a subir. */
  incompleto: boolean;
}

/**
 * Estado de la evidencia del traslado. Lo que "falta" son las fotos que
 * Activaciones eliminó (marcas) y el técnico todavía no repone.
 */
export function resumenFotosTraslado(ordenTrabajo: any): ResumenFotosTraslado {
  const faltanIngreso = eliminadasIngresoDe(ordenTrabajo).length;
  const faltanFinalizacion = eliminadasFinalizacionDe(ordenTrabajo).length;
  const procesoEliminadas = FOTOS_PROCESO_SLOTS.filter((s) => eliminadaProcesoPorTipo(ordenTrabajo, s.tipo)).map(
    (s) => s.titulo,
  );
  return {
    ingreso: fotosIngresoDe(ordenTrabajo).length,
    proceso: FOTOS_PROCESO_SLOTS.length - fotosProcesoFaltantes(ordenTrabajo).length,
    finalizacion: fotosFinalizacionDe(ordenTrabajo).length,
    iniciado: fotosTrasladoIniciado(ordenTrabajo),
    finalizado: fotosTrasladoFinalizado(ordenTrabajo),
    faltanIngreso,
    procesoEliminadas,
    faltanFinalizacion,
    incompleto: faltanIngreso > 0 || faltanFinalizacion > 0 || procesoEliminadas.length > 0,
  };
}

/** Texto corto de lo que falta volver a subir ("1 de ingreso, Foto punto final"). */
export function textoFotosFaltantes(r: ResumenFotosTraslado): string {
  const partes: string[] = [];
  if (r.faltanIngreso > 0) partes.push(`${r.faltanIngreso} de ingreso`);
  if (r.procesoEliminadas.length > 0) partes.push(r.procesoEliminadas.join(" y "));
  if (r.faltanFinalizacion > 0) partes.push(`${r.faltanFinalizacion} de finalización`);
  return partes.join(", ");
}

/** "por Juan Pérez · 06/10/2026 11:20" para mostrar bajo una foto eliminada. */
export function textoEliminadaPor(marca?: FotoTrasladoEliminada | null): string {
  if (!marca) return "";
  const partes: string[] = [];
  if (marca.eliminada_por) partes.push(`por ${marca.eliminada_por}`);
  if (marca.eliminada_fecha) partes.push(dayjs(marca.eliminada_fecha).format("DD/MM/YYYY HH:mm"));
  return partes.join(" · ");
}
